import { memo } from "react";

import {
  createHashRouter,
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
} from "react-router-dom";

import { useAtomValue } from "jotai";

import { isElectronGuest } from "@/services/auth.service";

import loadable from "@loadable/component";

import { useProtocolAuth } from "./hooks/useProtocolAuth";

import { useGuestPairing } from "./hooks/useGuestPairing";

import { useSessionValidator } from "./hooks/useSessionValidator";

import { useInitialAuthValidation } from "./hooks/useInitialAuthValidation";

import { useRomHeartbeat } from "./hooks/useRomHeartbeat";

import { useRomLeaveOnUnload } from "./hooks/useRomLeaveOnUnload";

import { useRomCloseListener } from "./hooks/useRomCloseListener";

import { useSessionDisconnectListener } from "./hooks/useSessionDisconnectListener";

import { PairRequestDialog } from "./components/PairRequestDialog";

import { SessionExpiredModal } from "./components/SessionExpiredModal";

import { isAuthenticatedAtom } from "./atoms/auth.atoms";

const RouteLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
  </div>
);

const Login = loadable(() => import("./screens/Login"), {
  fallback: <RouteLoadingFallback />,
});

const Register = loadable(() => import("./screens/Register"), {
  fallback: <RouteLoadingFallback />,
});

const ForgotPassword = loadable(() => import("./screens/ForgotPassword"), {
  fallback: <RouteLoadingFallback />,
});

const Welcome = loadable(() => import("./screens/Welcome"), {
  fallback: <RouteLoadingFallback />,
});

const Home = loadable(() => import("./screens/Home"), {
  fallback: <RouteLoadingFallback />,
});

const CameraConfig = loadable(() => import("./screens/CameraConfig"), {
  fallback: <RouteLoadingFallback />,
});

const ScreenShareConfig = loadable(
  () => import("./screens/ScreenShareConfig"),

  {
    fallback: <RouteLoadingFallback />,
  },
);

const Settings = loadable(() => import("./screens/Settings"), {
  fallback: <RouteLoadingFallback />,
});

const Permissions = loadable(() => import("./screens/Permissions"), {
  fallback: <RouteLoadingFallback />,
});

const Discovery = loadable(() => import("./screens/Discovery"), {
  fallback: <RouteLoadingFallback />,
});

const ConnectionMode = loadable(() => import("./screens/ConnectionMode"), {
  fallback: <RouteLoadingFallback />,
});

const JoinRemote = loadable(() => import("./screens/JoinRemote"), {
  fallback: <RouteLoadingFallback />,
});

const AuthCallback = loadable(() => import("./screens/AuthCallback"), {
  fallback: <RouteLoadingFallback />,
});

const Scan = loadable(() => import("./screens/Scan"), {
  fallback: <RouteLoadingFallback />,
});

const Join = loadable(() => import("./screens/Join"), {
  fallback: <RouteLoadingFallback />,
});

function GuestLayout() {
  useProtocolAuth();

  useInitialAuthValidation();

  useRomHeartbeat();

  useRomLeaveOnUnload();

  useRomCloseListener();

  useSessionDisconnectListener();

  const { pairRequest, respondToPairRequest } = useGuestPairing();

  const sessionExpired = useSessionValidator();

  return (
    <div className="bg-background text-foreground">
      <Outlet />
      {pairRequest && (
        <PairRequestDialog
          hostName={pairRequest.hostName}
          hostIp={pairRequest.hostIp}
          hostPort={pairRequest.hostPort}
          requestId={pairRequest.requestId}
          onRespond={respondToPairRequest}
        />
      )}
      <SessionExpiredModal open={sessionExpired} />
    </div>
  );
}

/** Redirects to /home (if saved session) or /discover if already authenticated */

function AuthRedirect() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (isAuthenticated) {
    const hasSession =
      localStorage.getItem("@Guest/serverHost") &&
      localStorage.getItem("@Guest/serverPort");

    return <Navigate to={hasSession ? "/home" : "/connection-mode"} replace />;
  }

  return <Login />;
}

/** Redirects to / (login) if not authenticated */

function RequireAuth() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (!isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}

// El guest web (guest.eriol.io, nginx con SPA fallback) usa history routing —
// URLs limpias (/connection-mode). El guest Electron corre sobre file://, que NO
// soporta history routing → ahí sigue con hash. Compat: un link viejo con hash
// (#/join/<id>, #/auth-callback?…) se reescribe al path real al cargar, así QRs
// y links ya compartidos no se rompen con el cambio.
if (
  typeof window !== "undefined" &&
  !isElectronGuest() &&
  window.location.hash.startsWith("#/")
) {
  window.history.replaceState(null, "", window.location.hash.slice(1));
}

const createGuestRouter =
  typeof window !== "undefined" && isElectronGuest()
    ? createHashRouter
    : createBrowserRouter;

const router = createGuestRouter([
  {
    element: <GuestLayout />,

    children: [
      // Public routes

      { path: "/", element: <AuthRedirect /> },

      { path: "/login", element: <Login /> },

      { path: "/register", element: <Register /> },

      { path: "/forgot-password", element: <ForgotPassword /> },

      { path: "/auth-callback", element: <AuthCallback /> },

      { path: "/scan", element: <Scan /> },

      { path: "/join/:romId", element: <Join /> },

      // Protected routes

      {
        element: <RequireAuth />,

        children: [
          { path: "/welcome", element: <Welcome /> },

          { path: "/home", element: <Home /> },

          { path: "/camera-config", element: <CameraConfig /> },

          { path: "/screen-config", element: <ScreenShareConfig /> },

          { path: "/settings", element: <Settings /> },

          { path: "/permissions", element: <Permissions /> },

          { path: "/discover", element: <Discovery /> },

          { path: "/connection-mode", element: <ConnectionMode /> },

          { path: "/join", element: <JoinRemote /> },
        ],
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default memo(App);
